import { useRef, useState } from 'react';
import { Paperclip, Upload, X } from 'lucide-react';
import { api, ApiError } from '../lib/api';
import { Button } from './ui';

/**
 * Escolhe e envia **um** arquivo, devolvendo o id.
 *
 * Um componente só, e não uma cópia por formulário: o envio é em dois passos —
 * o arquivo sobe para a loja, o id vai junto do cadastro — e essa sequência
 * escrita quatro vezes divergiria no primeiro tratamento de erro.
 *
 * O envio acontece **na escolha**, e não no salvamento do formulário. É
 * deliberado: um PDF de 40 MB subindo enquanto a pessoa ainda digita a data é
 * tempo que ela não percebe; subindo depois que ela clica em "Salvar", é uma
 * tela travada sem explicação.
 *
 * O preço é o arquivo órfão quando alguém desiste do formulário: ocupa espaço no
 * acervo e nada mais. Na ordem inversa, o cadastro nasceria afirmando ter um
 * documento que não existe — que é pior.
 */

/** Espelha a whitelist de `files.service.ts`. */
const ACCEPT = '.pdf,.png,.jpg,.jpeg,.docx,.xlsx,.zip';

export function FilePicker({
  storeId,
  label = 'Arquivo',
  value,
  fileName,
  onChange,
}: {
  storeId: string;
  label?: string;
  /** O id já vinculado, quando o cadastro está sendo editado. */
  value: string | null;
  /** Nome do que já está anexado, para a pessoa reconhecer o que trocaria. */
  fileName?: string | null;
  onChange: (fileId: string | null, fileName: string | null) => void;
}) {
  const input = useRef<HTMLInputElement>(null);
  const [nome, setNome] = useState<string | null>(fileName ?? null);
  const [enviando, setEnviando] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  async function enviar(file: File) {
    setEnviando(true);
    setErro(null);
    try {
      const enviado = await api.upload<{ id: string; originalName: string }>(
        `/stores/${storeId}/files`,
        file,
      );
      setNome(enviado.originalName);
      onChange(enviado.id, enviado.originalName);
    } catch (err) {
      setErro(err instanceof ApiError ? err.message : 'Não foi possível enviar o arquivo.');
    } finally {
      setEnviando(false);
      if (input.current) input.current.value = '';
    }
  }

  return (
    <div className="space-y-1.5">
      <p className="text-sm font-medium text-slate-700">{label}</p>

      <div className="flex flex-wrap items-center gap-2">
        <input
          ref={input}
          type="file"
          accept={ACCEPT}
          className="sr-only"
          onChange={(event) => {
            const file = event.target.files?.[0];
            if (file) void enviar(file);
          }}
        />
        <Button
          type="button"
          variant="outline"
          size="sm"
          loading={enviando}
          onClick={() => input.current?.click()}
        >
          <Upload className="size-4" aria-hidden />
          {value ? 'Trocar arquivo' : 'Escolher arquivo'}
        </Button>

        {value && (
          <>
            <a
              href={`/api/files/${value}`}
              className="flex items-center gap-1 text-sm text-brand-700 underline-offset-4 hover:underline"
            >
              <Paperclip className="size-4" aria-hidden />
              {nome ?? 'arquivo anexado'}
            </a>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              aria-label="Remover o anexo deste cadastro"
              onClick={() => {
                setNome(null);
                onChange(null, null);
              }}
            >
              <X className="size-4" aria-hidden />
            </Button>
          </>
        )}
      </div>

      {erro && <p className="text-xs font-medium text-red-600">{erro}</p>}
      <p className="text-xs text-slate-500">PDF, imagem, DOCX, XLSX ou ZIP.</p>
    </div>
  );
}
